import { useCallback, useEffect, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import Webcam from 'react-webcam';
import { Close, FlashOff, FlashOn, PhotoLibrary } from '@mui/icons-material';
import { Box, IconButton, styled } from '@mui/material';

import { ImageCaptureIcon } from '~assets/icons';
import { VisuallyHiddenInput } from '~components/atoms/photo-upload';
import { CAMERA_STEP, useCameraContext } from '~providers/CameraProvider';
import { toastError } from '~utils/toast.util';

const videoConstraints = {
  facingMode: { ideal: 'environment' }, // Use the back camera if available, else use the front camera
  width: { ideal: 3840 }, // Get the highest resolution available (maximum 4K), the height will be calculated automatically
};

export const IconButtonWhite = styled(IconButton)({
  color: 'white',
  p: 8,
});

export const webcamStyle = (isLandscape: boolean): React.CSSProperties => ({
  position: 'fixed',
  top: 0,
  left: 0,
  width: '100%',
  height: '100%',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  backgroundColor: 'black',
  flexDirection: isLandscape ? 'row' : 'column',
  zIndex: 1000,
});

const commonBoxStyle = (
  isVisible: boolean,
  isLandscapeView = true,
): React.CSSProperties => ({
  display: 'flex',
  visibility: isVisible ? 'visible' : 'hidden',
  zIndex: 1001,
  width: isLandscapeView ? 'auto' : '100%',
  height: isLandscapeView ? '100%' : 'auto',
});

export const Camera: React.FC<{ isVisible?: boolean }> = ({
  isVisible = false,
}) => {
  const {
    selectedImageSrc,
    handleSetCameraStep,
    closeCamera,
    isCameraReady,
    handleSetCameraReady,
    handleSetSelectedImageSrc,
    handleSetErrorOnLoadingCamera,
    errorOnLoadingCamera,
    onChangeFile,
    isLandscapeView,
  } = useCameraContext();

  const webcamRef = useRef<Webcam | null>(null);
  const [flashOn, setFlashOn] = useState(false); // Toggle for brightness/flash
  const [isLandscape, setIsLandscape] = useState(false); // State to track device orientation

  // Check for orientation changes
  const handleOrientationChange = useCallback(() => {
    setIsLandscape(window.matchMedia('(orientation: landscape)').matches);
  }, []);

  // Apply brightness filter to the captured image using a canvas
  const applyBrightnessToCapturedImage = useCallback(
    (imageSrc: string): Promise<string> => {
      if (!flashOn) return Promise.resolve(imageSrc);

      const canvas = document.createElement('canvas');
      const context = canvas.getContext('2d');
      const img = new Image();
      img.src = imageSrc;

      return new Promise<string>((resolve) => {
        img.onload = () => {
          canvas.width = img.width;
          canvas.height = img.height;

          if (context) {
            context.filter = flashOn ? 'brightness(2)' : 'brightness(1)';
            context.drawImage(img, 0, 0);
            resolve(canvas.toDataURL('image/jpeg'));
          } else {
            resolve(imageSrc);
          }
        };
      });
    },
    [flashOn],
  );

  const imageToBase64 = useCallback(
    (video: HTMLVideoElement): Promise<string> => {
      return new Promise((resolve, reject) => {
        if (!video || video.videoWidth === 0 || video.videoHeight === 0) {
          return reject('Invalid video source');
        }

        const canvas = document.createElement('canvas');
        canvas.width = video.videoWidth;
        canvas.height = video.videoHeight;
        const context = canvas.getContext('2d');

        if (context) {
          context.drawImage(video, 0, 0, canvas.width, canvas.height);
          resolve(canvas.toDataURL('image/jpeg', 1.0)); // Full quality
        } else {
          reject('Failed to get canvas context');
        }
      });
    },
    [],
  );

  // Capture image from webcam
  const capture = useCallback(async () => {
    if (webcamRef.current && isCameraReady) {
      // Take a snapshot of the video stream
      const video = webcamRef.current.video;
      if (!video) return null;
      try {
        // Convert the captured image to base64 String
        const imageSrc = await imageToBase64(video);

        // Apply brightness to the captured image
        const brightenedImageSrc = await applyBrightnessToCapturedImage(
          imageSrc,
        );

        handleSetSelectedImageSrc(brightenedImageSrc);
      } catch (error) {
        toastError({
          error,
          appMessage: 'Failed to capture image. Please try again.',
        });
      }
    } else {
      // eslint-disable-next-line no-console
      console.warn('Camera is not ready yet!');
    }
  }, [
    isCameraReady,
    imageToBase64,
    applyBrightnessToCapturedImage,
    handleSetSelectedImageSrc,
  ]);

  useEffect(() => {
    if (isLandscapeView) {
      toast.info(
        'It’s recommended to take this station photo in landscape view.',
      );
    }
  }, [isLandscapeView]);

  useEffect(() => {
    if (selectedImageSrc) handleSetCameraStep(CAMERA_STEP.PREVIEW);
  }, [handleSetCameraStep, selectedImageSrc]);

  // Listen for orientation changes on component mount
  useEffect(() => {
    window.addEventListener('resize', handleOrientationChange);

    // Initial check for orientation
    handleOrientationChange();

    // Cleanup the event listener on unmount
    return () => {
      window.removeEventListener('resize', handleOrientationChange);
    };
  }, [handleOrientationChange]);

  if (errorOnLoadingCamera) {
    closeCamera();
    // eslint-disable-next-line no-console
    console.error(`Loading camera failed with error: ${errorOnLoadingCamera}`);
    toastError({
      error: errorOnLoadingCamera,
      appMessage: 'Loading camera failed',
    });
    return null;
  }

  return (
    <>
      {!isCameraReady && <p>Loading camera...</p>}
      <Webcam
        audio={false}
        ref={webcamRef}
        screenshotFormat="image/jpeg"
        videoConstraints={videoConstraints}
        style={{
          ...webcamStyle(isLandscape), // Apply style based on orientation
          filter: flashOn ? 'brightness(2)' : 'brightness(1)',
          visibility: isVisible ? 'visible' : 'hidden',
        }}
        onUserMedia={() => handleSetCameraReady(true)}
        onUserMediaError={(e) => handleSetErrorOnLoadingCamera(e.toString())}
        screenshotQuality={1}
      />
      <Box
        sx={{
          ...commonBoxStyle(isVisible, false),
          justifyContent: 'space-between',
          alignItems: 'center',
          position: 'absolute',
          zIndex: 1002,
          top: isLandscape ? 0 : '45px',
          left: 0,
          px: isLandscape ? 9 : 1,
          pb: isLandscape ? 5 : 0,
        }}
      >
        {/* Close Button (Top left) */}
        <IconButtonWhite onClick={closeCamera}>
          <Close />
        </IconButtonWhite>

        {/* Flash Button (Top right) */}
        <IconButtonWhite onClick={() => setFlashOn((prev) => !prev)}>
          {flashOn ? <FlashOn /> : <FlashOff />}
        </IconButtonWhite>
      </Box>
      <Box
        sx={{
          ...commonBoxStyle(isVisible, isLandscape),
          flexDirection: isLandscape ? 'column-reverse' : 'row',
          justifyContent: 'space-between',
          alignItems: 'center',
          position: 'absolute',
          bottom: 0,
          right: isLandscape ? 0 : undefined,
          left: isLandscape ? undefined : 0,
          px: isLandscape ? 0 : 7.5,
          py: isLandscape ? 7.5 : 0,
          pb: isLandscape ? undefined : 7.5,
          pr: isLandscape ? 7.5 : undefined,
        }}
      >
        {/* Gallery Button */}
        <IconButton component="label">
          <PhotoLibrary fontSize="large" sx={{ color: 'white' }} />
          <VisuallyHiddenInput type="file" onChange={onChangeFile} />
        </IconButton>

        {/* Capture Button */}
        <IconButtonWhite disabled={!isCameraReady} onClick={capture}>
          <ImageCaptureIcon />
        </IconButtonWhite>

        {/* Hidden Placeholder IconButton */}
        <IconButton style={{ visibility: 'hidden' }}>
          <PhotoLibrary fontSize="large" />
        </IconButton>
      </Box>
    </>
  );
};
